/**
 * Where the menu should open when it next mounts.
 *
 * My orders is the last thing in the menu, so the only way to reach it from
 * there is with the list already at the bottom — opening back at the bottom is
 * where the customer actually was, without having to remember a scroll offset.
 *
 * The section they were in travels with it. The strip marks that section, and
 * the scroll would only report it once the restored position has settled — one
 * frame after the strip has painted — so the mark is carried instead and the
 * strip opens on the right chip with nothing to correct.
 *
 * Deliberately in memory rather than in storage: leaving for My orders and
 * coming back keeps this module, so the menu reopens at the foot; a refresh
 * reloads it, so a refresh still opens at the top where it should.
 */

/** The foot the customer left from, with the section they were in. */
let footReturn: { category: string | null } | null = null;

/**
 * Records that the customer left the menu for My orders, from its foot.
 *
 * @param category The section the strip was marking on the way out.
 */
export function rememberMenuFoot(category: string | null): void {
  footReturn = { category };
}

/**
 * Reads the section to open on, without clearing the flag.
 *
 * For the render that has to know where the menu is about to open *before* it
 * paints. A render cannot take the flag — it may be repeated, and the second
 * read would come back empty. The effect takes it.
 *
 * @returns The section the menu was left on, or null when it is not returning.
 */
export function peekMenuFootCategory(): string | null {
  return footReturn?.category ?? null;
}

/**
 * Reads the flag, and clears it so it is honoured once.
 *
 * @returns Whether the menu should open at its foot.
 */
export function takeMenuFoot(): boolean {
  const wanted = footReturn !== null;
  footReturn = null;
  return wanted;
}
